const normalize = (value) => `${value || ""}`.trim().toLowerCase();
const closedStatuses = new Set(["done", "completed", "archived", "cancelled", "canceled", "closed", "resolved", "approved", "rejected", "paid", "refunded", "won", "lost", "published", "exported", "ready", "duplicate"]);
const reviewStatuses = new Set(["review", "in review", "client review", "pending", "pending approval", "awaiting approval", "needs review", "requested"]);
const workModules = new Set(["work", "tasks & tickets", "my work"]);

export const isOverviewTicket = (record) => workModules.has(normalize(record.moduleName))
  && !["epic", "sprint", "project", "approval", "automation", "template"].includes(normalize(record.type))
  && !["epics", "sprints", "projects", "approvals", "automation", "templates"].includes(normalize(record.tab));
export const isOverviewClosed = (record) => closedStatuses.has(normalize(record.status));
export const isOverviewApproval = (record) => !isOverviewClosed(record) && reviewStatuses.has(normalize(record.status)) && Boolean(record.approvalRole);

const scopeMembers = (user, members) => [
  { ...user, userId: user?.userId || user?._id || user?.id, allProjects: true },
  ...(members || []),
];
const matchesProject = (record, member) => member.allProjects || (record.projectId && member.projectIds?.length
  ? member.projectIds.includes(record.projectId)
  : (member.projectRefs || []).some((project) => normalize(project) === normalize(record.projectRef)));
const matchesAssignment = (record, member) => matchesProject(record, member)
  && (Boolean(record.ownerUserId && member.userId && `${record.ownerUserId}` === `${member.userId}`)
    || [record.owner, ...(Array.isArray(record.assignees) ? record.assignees : [])]
      .some((value) => Boolean(normalize(value)) && [normalize(member.name), normalize(member.email)].includes(normalize(value))));

export const canReviewOverviewRecord = (record, user) => isOverviewApproval(record) && `${record.approvalRole}`.split("|")
  .some((value) => [normalize(user?.name), normalize(user?.email)].filter(Boolean).includes(normalize(value))
    || (normalize(value) === "workspace owner" && ["owner", "admin"].includes(normalize(user?.accessLevel))));

export function getOverviewRecords(records, currentUser, teamMembers = null) {
  const members = scopeMembers(currentUser, teamMembers);
  const directlyRelated = records.filter((record) => members.some((member) => matchesAssignment(record, member)
    || (matchesProject(record, member) && canReviewOverviewRecord(record, member))));
  const related = new Set(directlyRelated);
  const work = directlyRelated.filter(isOverviewTicket);
  return records.filter((record) => related.has(record) || work.some((item) => {
    if (record.moduleName === "Projects") return item.projectId ? item.projectId === record.id : Boolean(item.projectRef && item.projectRef === record.title);
    if (item.projectId && record.projectId ? item.projectId !== record.projectId : item.projectRef !== record.projectRef) return false;
    return (record.type === "Sprint" && Boolean(item.sprint) && item.sprint === record.title)
      || (record.type === "Epic" && Boolean(item.epic) && item.epic === record.title);
  }));
}

export function getOverviewActivity(activity, currentUser, teamMembers, scopedRecords) {
  const members = scopeMembers(currentUser, teamMembers);
  const recordIds = new Set(scopedRecords.map((record) => record.id));
  return activity.filter((item) => members.some((member) => {
    const sameUser = item.userId && member.userId ? `${item.userId}` === `${member.userId}` : Boolean(normalize(item.userName) && normalize(item.userName) === normalize(member.name));
    return sameUser && (member.allProjects || Boolean(item.recordId && recordIds.has(item.recordId)));
  }));
}

export function getPersonalAttentionItems(records, currentUser, todayKey, teamMembers = null) {
  const members = scopeMembers(currentUser, teamMembers);
  const items = new Map();

  for (const record of records) {
    const status = normalize(record.status);
    if (closedStatuses.has(status)) continue;
    const assigned = members.some((member) => matchesAssignment(record, member));
    const isTicket = isOverviewTicket(record);
    const needsMyReview = members.some((member) => matchesProject(record, member) && canReviewOverviewRecord(record, member));
    const due = /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(record.due || "") ? record.due.slice(0, 10) : "";
    let signal;
    if (assigned && isTicket && ["blocked", "on hold", "stuck"].includes(status)) signal = { rank: 0, actionNote: "Blocked", actionTone: "blocked" };
    else if (assigned && isTicket && due && due < todayKey) signal = { rank: 1, actionNote: "Overdue", actionTone: "overdue" };
    else if (needsMyReview) signal = { rank: 2, actionNote: teamMembers ? "Approval needed" : "Your approval", actionTone: "review" };
    else if (assigned && isTicket && due === todayKey) signal = { rank: 3, actionNote: "Due today", actionTone: "normal" };
    else if (assigned && isTicket && (record.flagged || ["urgent", "critical", "highest", "high"].includes(normalize(record.priority)))) signal = { rank: 4, actionNote: record.flagged ? "Flagged" : `${record.priority} priority`, actionTone: "normal" };
    if (!signal) continue;
    items.set(record.id || record._id || record, { ...record, ...signal, sortDue: due || "9999-12-31" });
  }

  return [...items.values()]
    .sort((left, right) => left.rank - right.rank || left.sortDue.localeCompare(right.sortDue) || `${left.title || ""}`.localeCompare(`${right.title || ""}`))
    .slice(0, 6);
}
